import { Button } from "@svea/design-system-components-react";
import {
	ButtonGroup,
	FormHelperText,
	Grid,
	P,
	Stack,
	Text,
	useText,
	FormControlLabel,
	Checkbox,
	Markdown,
} from "@sveabank/cc-common";
import { useState } from "react";
import { usePageContext } from "../../../../hooks/context";
import { useClcCompanyApplication } from "../../hooks";
import ClcCards from "./clc-cards";
import ClcPackages from "./clc-packages";

type Props = {
	onSubmit: () => Promise<void>; // todo: no submit endpoint yet
	onCancel: () => void;
};

const ClcProducts = ({ onSubmit, onCancel }: Props) => {
	const [validationMessage, setValidationMessage] = useState<string | null>(null);
	const [hasForetagarnaConsent, setHasForetagarnaConsent] = useState(false);
	const [showForetagarnaConsentError, setShowForetagarnaConsentError] = useState(false);

	const {
		selectedPackage,
		cards: {
			debitCards: { cards },
		},
		isConfirmingProducts,
	} = usePageContext(useClcCompanyApplication);

	const [t, texts] = useText();

	const isForetagarnaPackage = selectedPackage?.packageId === "FÖRETAGARNA-SVEA-STARTA-BOLAG";
	const checkboxLabel = t(texts.newCompany.packages["FÖRETAGARNA-SVEA-STARTA-BOLAG"].checkboxLabel);

	const includedCard = cards.find((c) => c.isAddon === false);
	if (!includedCard && selectedPackage)
		throw new Error("Unexpected: No card included in package (products)");

	const hasUnconfiguredCard = cards.some((c) => !c.isAddon && !c.isConfigured);

	const onContinueClick = () => {
		if (hasUnconfiguredCard) {
			setValidationMessage(t(texts.newCompany.products.unconfiguredCardError));
			return;
		}
		if (isForetagarnaPackage && !hasForetagarnaConsent) {
			setShowForetagarnaConsentError(true);
			return;
		}
		setValidationMessage(null);
		onSubmit();
	};

	if (!selectedPackage) {
		return null;
	}

	return (
		<Grid>
			<Grid.Col
				span={{
					base: 12,
					tablet: 8,
				}}
			>
				<Stack gap={8}>
					{isForetagarnaPackage && (
						<Text className="text-xl font-bold">
							{t(texts.newCompany.packages["FÖRETAGARNA-SVEA-STARTA-BOLAG"].chooseHeading)}
						</Text>
					)}
					<P>{t(texts.newCompany.packages["SVEA-STARTA-BOLAG"].intro)}</P>

					<ClcPackages />
					<ClcCards />

					{isForetagarnaPackage && (
						<Stack gap={4}>
							<FormControlLabel label={<Markdown>{checkboxLabel}</Markdown>} gap={4}>
								<div>
									<Checkbox
										checked={hasForetagarnaConsent}
										onChange={(event) => {
											setHasForetagarnaConsent(!!event.target.checked);
											if (event.target.checked) setShowForetagarnaConsentError(false);
										}}
										name="foretagarna-consent"
									/>
								</div>
							</FormControlLabel>

							{showForetagarnaConsentError && !hasForetagarnaConsent && (
								<FormHelperText error>
									{t(texts.newCompany.packages["FÖRETAGARNA-SVEA-STARTA-BOLAG"].consentRequired)}
								</FormHelperText>
							)}
						</Stack>
					)}

					<Stack gap={2}>
						<ButtonGroup row align="start">
							<Button type="button" variant="secondary" onPress={onCancel}>
								{t(texts.common.abort)}
							</Button>
							<Button
								isPending={isConfirmingProducts}
								onPress={onContinueClick}
								data-tracking-action="CLC Application Continue"
								data-tracking-name="Products"
							>
								{t(texts.common.continue)}
							</Button>
						</ButtonGroup>
						{validationMessage && hasUnconfiguredCard && (
							<FormHelperText error>{validationMessage}</FormHelperText>
						)}
					</Stack>
				</Stack>
			</Grid.Col>
		</Grid>
	);
};

export default ClcProducts;
